import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  DragDropContext,
  Draggable,
  Droppable,
} from "@hello-pangea/dnd";
import { toast } from "react-toastify";

import { postRightworkAPI } from "../../requests/utilities/requests";
import { API_save_summary_card_preferences } from "../../../../constants";
import {
  SUMMARY_CARD_REGISTRY,
  DEFAULT_CARD_ORDER,
  sanitizeCardOrder,
} from "./summaryCardRegistry";

const AVAILABLE_DROPPABLE = "available";
const SLOT_DROPPABLE_PREFIX = "slot-";

function slotIndexFromDroppableId(id) {
  if (typeof id !== "string" || !id.startsWith(SLOT_DROPPABLE_PREFIX)) return -1;
  return parseInt(id.slice(SLOT_DROPPABLE_PREFIX.length), 10);
}

function buildAvailableIds(slots, laborLawEnabled) {
  const flagValues = { laborLawEnabled: !!laborLawEnabled };
  return Object.keys(SUMMARY_CARD_REGISTRY).filter((id) => {
    if (slots.includes(id)) return false;
    const entry = SUMMARY_CARD_REGISTRY[id];
    // featureFlag gates the entire card (both sides are useless without it).
    // actualGated only gates the actual side; the planned side may still be
    // useful (e.g. violations falls back to a Conflicts view), so do NOT
    // filter on actualGated alone.
    if (entry.featureFlag && !flagValues[entry.featureFlag]) {
      return false;
    }
    return true;
  });
}

function getPlannedLabelText(entry, previewCtx) {
  if (!entry.planned) return "—";
  return entry.planned.cardLabel(null, previewCtx);
}

function getActualLabelText(entry, previewCtx) {
  if (!entry.actual) return "No corresponding actual";
  if (entry.actualGated && !previewCtx[entry.actualGated]) {
    return "Not available";
  }
  return entry.actual.cardLabel(null, previewCtx);
}

function CardTile({
  id,
  dragHandleProps,
  isDragging,
  previewCtx,
  showDragHandle = true,
  onRemove,
  hideRemove = false,
}) {
  const entry = SUMMARY_CARD_REGISTRY[id];
  if (!entry) return null;
  const Icon = entry.icon;
  const plannedLabel = getPlannedLabelText(entry, previewCtx);
  const actualLabel = getActualLabelText(entry, previewCtx);
  const actualMuted = !entry.actual || (entry.actualGated && !previewCtx[entry.actualGated]);

  return (
    <div
      className={`group relative w-[196px] shrink-0 rounded-2xl border bg-white flex flex-col select-none ${
        isDragging
          ? "border-violet-400 shadow-lg"
          : "border-slate-200 shadow-sm"
      }`}
    >
      {onRemove && !hideRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white border border-slate-300 text-slate-500 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10"
          aria-label="Remove card from slot"
        >
          <CloseIcon style={{ fontSize: 12 }} />
        </button>
      )}
      <div
        className={`flex items-center gap-2 px-3 pt-2.5 pb-2 min-h-[3rem] ${
          showDragHandle ? "cursor-grab active:cursor-grabbing" : ""
        }`}
        {...(showDragHandle ? dragHandleProps || {} : {})}
      >
        {showDragHandle && (
          <DragIndicatorIcon
            className="self-start"
            style={{ fontSize: 16, color: "#94a3b8", marginTop: 4 }}
          />
        )}
        <div className="h-7 w-7 rounded-full bg-violet-50 text-violet-700 flex items-center justify-center shrink-0">
          <Icon style={{ fontSize: 16 }} />
        </div>
        <span className="text-[12px] font-semibold text-slate-900 leading-tight break-words">
          {entry.label}
        </span>
      </div>
      <div className="border-t border-slate-100 px-3 py-2 flex flex-col items-center gap-0.5">
        <span className="text-[11px] text-slate-700 leading-tight text-center">
          {plannedLabel}
        </span>
        <KeyboardArrowDownIcon
          style={{ fontSize: 14, color: "#cbd5e1" }}
        />
        <span
          className={`text-[11px] leading-tight text-center ${
            actualMuted ? "text-slate-400 italic" : "text-slate-700"
          }`}
        >
          {actualLabel}
        </span>
      </div>
    </div>
  );
}

function CustomizeSummaryCardsModal({
  open,
  onClose,
  currentOrder,
  laborLawEnabled,
  onSaved,
}) {
  const [slots, setSlots] = useState(() => sanitizeCardOrder(currentOrder));
  const [available, setAvailable] = useState(() =>
    buildAvailableIds(sanitizeCardOrder(currentOrder), laborLawEnabled)
  );
  const [saving, setSaving] = useState(false);

  // Re-sync local state when the modal opens with a (possibly) new currentOrder.
  useEffect(() => {
    if (!open) return;
    const sanitized = sanitizeCardOrder(currentOrder);
    setSlots(sanitized);
    setAvailable(buildAvailableIds(sanitized, laborLawEnabled));
  }, [open, currentOrder, laborLawEnabled]);

  const previewCtx = useMemo(
    () => ({
      showLaborLawCard: !!laborLawEnabled,
      laborLawEnabled: !!laborLawEnabled,
      conflictsCount: 0,
      laborLawViolationsCount: 0,
    }),
    [laborLawEnabled]
  );

  const renderTileClone = (provided, snapshot, rubric) => {
    const style = snapshot.isDropAnimating
      ? { ...provided.draggableProps.style, transitionDuration: "0.001s" }
      : provided.draggableProps.style;
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        style={style}
      >
        <CardTile
          id={rubric.draggableId}
          dragHandleProps={provided.dragHandleProps}
          isDragging={true}
          previewCtx={previewCtx}
        />
      </div>
    );
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const destSlotIdx = slotIndexFromDroppableId(destination.droppableId);

    // available → slot: place card in slot; displaced card (if any) returns to available
    if (
      source.droppableId === AVAILABLE_DROPPABLE &&
      destSlotIdx >= 0
    ) {
      const draggedId = available[source.index];
      const displacedId = slots[destSlotIdx];
      const newSlots = [...slots];
      newSlots[destSlotIdx] = draggedId;
      const newAvailable = [...available];
      newAvailable.splice(source.index, 1);
      if (displacedId) newAvailable.push(displacedId);
      setSlots(newSlots);
      setAvailable(newAvailable);
      return;
    }
  };

  const removeFromSlot = (idx) => {
    const cardId = slots[idx];
    if (!cardId) return;
    const newSlots = [...slots];
    newSlots[idx] = null;
    setSlots(newSlots);
    setAvailable([...available, cardId]);
  };

  const handleResetToDefaults = () => {
    setSlots(DEFAULT_CARD_ORDER);
    setAvailable(buildAvailableIds(DEFAULT_CARD_ORDER, laborLawEnabled));
  };

  const handleClearAll = () => {
    const slotCards = slots.filter(Boolean);
    setSlots([null, null, null, null, null]);
    setAvailable([...available, ...slotCards]);
  };

  const handleSave = async () => {
    if (slots.length !== 5 || slots.some((id) => !id)) return;
    setSaving(true);
    try {
      const res = await postRightworkAPI(API_save_summary_card_preferences, {
        card_ids: slots,
      });
      if (res.status === 200) {
        toast.success("Summary cards saved", { autoClose: 2000 });
        onSaved(slots);
        onClose();
      } else {
        toast.error("Could not save summary cards", { autoClose: 3000 });
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.system?.message || "Could not save summary cards",
        { autoClose: 3000 }
      );
    } finally {
      setSaving(false);
    }
  };

  const isDirty = useMemo(
    () =>
      slots.length !== currentOrder.length ||
      slots.some((id, i) => id !== currentOrder[i]),
    [slots, currentOrder]
  );

  const hasEmptySlot = useMemo(() => slots.some((id) => !id), [slots]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: { xs: "95vw", md: 1200 },
          maxHeight: "92vh",
          bgcolor: "white",
          borderRadius: 3,
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Header */}
          <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-slate-100">
            <div>
              <h2 className="text-slate-900 text-lg font-semibold">
                Customize summary cards
              </h2>
              <p className="text-slate-500 text-xs mt-1">
                Drag a metric into a slot, or hover a slot card to remove it.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center"
              aria-label="Close"
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-6">
            {/* Available */}
            <section>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Available Metrics
              </div>
              <Droppable
                droppableId={AVAILABLE_DROPPABLE}
                isDropDisabled
                renderClone={renderTileClone}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex flex-wrap gap-3 p-2 rounded-xl border-2 min-h-[100px] max-w-[1128px] ${
                      snapshot.isDraggingOver
                        ? "border-violet-300 bg-violet-50/40"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    {available.length === 0 && (
                      <div className="w-full text-center text-[11px] text-slate-400 italic py-4">
                        All metrics are in active slots.
                      </div>
                    )}
                    {available.map((id, idx) => (
                      <Draggable key={id} draggableId={id} index={idx}>
                        {(p, s) => (
                          <div
                            ref={p.innerRef}
                            {...p.draggableProps}
                            style={p.draggableProps.style}
                          >
                            <CardTile
                              id={id}
                              dragHandleProps={p.dragHandleProps}
                              isDragging={s.isDragging}
                              previewCtx={previewCtx}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </section>

            {/* Active slots — each slot is its own independent droppable */}
            <section>
              <div className="flex items-baseline justify-between mb-2 max-w-[1128px]">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  Active Slots
                </div>
                <div className="text-[11px] text-slate-400">
                  {slots.filter(Boolean).length} of 5 selected
                </div>
              </div>

              <div className="flex gap-3 overflow-x-auto pb-1">
                {slots.map((id, idx) => (
                  <div key={idx} className="flex flex-col gap-1.5 shrink-0">
                    <div className="flex items-center gap-1.5 px-1">
                      <span className="h-5 w-5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-500 tracking-wide uppercase">
                        Slot {idx + 1}
                      </span>
                    </div>
                    <Droppable
                      droppableId={`${SLOT_DROPPABLE_PREFIX}${idx}`}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`relative p-2 rounded-xl border-2 border-dashed ${
                            snapshot.isDraggingOver
                              ? "border-violet-400 bg-violet-50/60"
                              : id
                              ? "border-slate-300 bg-slate-50"
                              : "border-slate-300 bg-slate-50/40"
                          }`}
                        >
                          {id ? (
                            <CardTile
                              id={id}
                              showDragHandle={false}
                              onRemove={() => removeFromSlot(idx)}
                              hideRemove={snapshot.isDraggingOver}
                              previewCtx={previewCtx}
                            />
                          ) : (
                            <div className="w-[196px] h-[112px] flex items-center justify-center">
                              <span className="text-[11px] text-slate-400 italic">
                                Drop a metric here
                              </span>
                            </div>
                          )}
                          <span
                            style={{
                              position: "absolute",
                              visibility: "hidden",
                              pointerEvents: "none",
                              top: 0,
                              left: 0,
                            }}
                          >
                            {provided.placeholder}
                          </span>
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResetToDefaults}
                className="text-[12px] text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-md hover:bg-slate-100"
              >
                Reset to defaults
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={slots.every((id) => !id)}
                className={`text-[12px] px-3 py-1.5 rounded-md ${
                  slots.every((id) => !id)
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
              >
                Clear all current metrics
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="text-[12px] text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-md hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !isDirty || hasEmptySlot}
                className={`text-[12px] font-medium px-4 py-1.5 rounded-md text-white ${
                  saving || !isDirty || hasEmptySlot
                    ? "bg-violet-300 cursor-not-allowed"
                    : "bg-violet-600 hover:bg-violet-700"
                }`}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </DragDropContext>
      </Box>
    </Modal>
  );
}

export default CustomizeSummaryCardsModal;
